import type { Carve, Part, PlacedPumpkin, Porch, PumpkinLight, PumpkinShape, Scene, SkinPattern, Stencil, Stroke } from '../types';
import { SCENE_VERSION, SCULPT_W, SCULPT_H } from '../types';
import { MAX_PUMPKINS } from './collide';

// A shared scene rebuilt from the fields the app knows, and nothing else (pumpkin#78). normaliseScene fills defaults
// but spreads what it is given, so on its own a share could carry anything. The render worker runs every new share
// through this before it is stored for good: unknown keys go, numbers must be finite and are clamped, strings match
// a pattern, lists are capped. Bounds are generous on purpose; they keep junk out, not shape the drawing.

type Obj = Record<string, unknown>;
// PHP stores {} as [], so an empty list stands for an empty object
const obj = (v: unknown): Obj | null => (Array.isArray(v) ? (v.length ? null : {}) : v && typeof v === 'object' ? (v as Obj) : null);
const num = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : undefined;
const req = (v: unknown, lo: number, hi: number, d: number) => num(v, lo, hi) ?? d;
const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);
const str = (v: unknown, re: RegExp): string | undefined => (typeof v === 'string' && re.test(v) ? v : undefined);
const oneOf = <T extends string>(v: unknown, all: readonly T[]): T | undefined => (all.includes(v as T) ? (v as T) : undefined);
const rgb = (v: unknown): [number, number, number] | undefined =>
  Array.isArray(v) && v.length === 3 && v.every(x => typeof x === 'number' && Number.isFinite(x)) ? (v.map(x => Math.min(8, Math.max(0, x))) as [number, number, number]) : undefined;
const pt = (v: unknown): [number, number] | undefined =>
  Array.isArray(v) && v.length === 2 && v.every(x => typeof x === 'number' && Number.isFinite(x)) ? [Math.min(16, Math.max(-16, v[0])), Math.min(16, Math.max(-16, v[1]))] : undefined;
const pts = (v: unknown, max: number): [number, number][] => (Array.isArray(v) ? v.slice(0, max).map(pt).filter(p => p !== undefined) : []);
/** drop the keys left undefined, so the stored JSON has only what was really there */
const tidy = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

const ID = /^[\w~-]{1,32}$/;
const LABEL = /^[\p{L}\p{N} '’&.,()-]{0,48}$/u;
const KINDS = ['field', 'tall', 'cinderella', 'ghost', 'jarrahdale', 'warty', 'mini', 'bottle', 'crookneck', 'turban', 'musquee',
  'shishigatani', 'pear', 'thorns', 'swan', 'bicolor', 'hound'] as const;
// an Int8 grid in base64: exactly the grid's size, nothing else rides along
const SCULPT = new RegExp(`^[A-Za-z0-9+/]{${Math.floor((SCULPT_W * SCULPT_H) / 3) * 4}}[A-Za-z0-9+/=]{4}$`);
const MAX_STROKES = 400, MAX_PARTS = 120, MAX_CARVES = 600, MAX_POINTS = 4000, MAX_HOLES = 64;

function pattern(v: unknown): SkinPattern | undefined {
  const p = obj(v);
  if (!p) return undefined;
  const o = tidy({ base2: rgb(p.base2), groove: rgb(p.groove), speckle: num(p.speckle, 0, 1), marble: num(p.marble, 0, 1), stripes: num(p.stripes, 0, 1),
    stripeN: num(p.stripeN, 1, 64), bicolor: num(p.bicolor, 0, 1), mottle: num(p.mottle, 0, 1) });
  return Object.keys(o).length ? o : undefined;
}

function shape(v: unknown): PumpkinShape | null {
  const s = obj(v), st = obj(s?.stem);
  const kind = oneOf(s?.kind, KINDS);
  if (!s || !kind) return null;
  return tidy({
    kind, seed: req(s.seed, -2147483648, 4294967296, 1), ribs: req(s.ribs, 1, 40, 10), ribDepth: req(s.ribDepth, 0, 2, 0.5), ribSharp: req(s.ribSharp, 0, 2, 0.5),
    squash: req(s.squash, 0.1, 4, 1), size: req(s.size, 0.02, 1, 0.16), lean: req(s.lean, -2, 2, 0), lopsided: req(s.lopsided, 0, 2, 0),
    warts: req(s.warts, 0, 2, 0), dimple: req(s.dimple, 0, 2, 0.5), neck: req(s.neck, 0, 2, 0),
    stem: tidy({ length: req(st?.length, 0, 4, 1), curl: req(st?.curl, -4, 4, 0), thick: req(st?.thick, 0, 4, 1), tilt: req(st?.tilt, -4, 4, 0), wild: num(st?.wild, 0, 1) }),
    variety: str(s.variety, LABEL), flaws: num(s.flaws, 0, 2), skin: rgb(s.skin) ?? [0.6, 0.2, 0.02], ribTint: req(s.ribTint, 0, 2, 0.3),
    sculpt: str(s.sculpt, SCULPT), waist: num(s.waist, 0, 2), fingers: num(s.fingers, 0, 2), flat: num(s.flat, 0, 2), well: num(s.well, 0, 2),
    pattern: pattern(s.pattern),
  });
}

function stroke(v: unknown): Stroke | null {
  const s = obj(v), tool = oneOf(s?.tool, ['saw', 'scraper', 'poker', 'eraser', 'pencil'] as const), id = str(s?.id, ID);
  if (!s || !tool || !id) return null;
  const holes = Array.isArray(s.holes) ? s.holes.slice(0, MAX_HOLES).map(h => pts(h, MAX_POINTS)).filter(h => h.length) : undefined;
  return tidy({ id, tool, width: req(s.width, 0, 4, 0.02), points: pts(s.points, MAX_POINTS), closed: !!s.closed,
    filled: bool(s.filled), outline: bool(s.outline), real: bool(s.real), holes: holes?.length ? holes : undefined });
}

function part(v: unknown): Part | null {
  const p = obj(v), tool = oneOf(p?.tool, ['saw', 'scraper'] as const), id = str(p?.id, ID), sh = str(p?.shape, ID);
  if (!p || !tool || !id || !sh) return null;
  return tidy({ id, shape: sh, tool, x: req(p.x, -4, 4, 0), y: req(p.y, -4, 4, 0), scale: req(p.scale, 0, 8, 1), rotate: req(p.rotate, -100, 100, 0),
    flip: !!p.flip, real: bool(p.real) });
}

function carve(v: unknown): Carve | null {
  const c = obj(v), tool = oneOf(c?.tool, ['knife', 'scraper', 'fill', 'poker'] as const), id = str(c?.id, ID);
  if (!c || !tool || !id) return null;
  return tidy({ id, tool: tool as Carve['tool'], width: req(c.width, 0, 4, 0.02), depth: req(c.depth, 0, 1, 1), clip: bool(c.clip), points: pts(c.points, MAX_POINTS),
    closed: !!c.closed, on: str(c.on, ID), flood: bool(c.flood) });
}

const list = <T>(v: unknown, max: number, f: (x: unknown) => T | null): T[] => (Array.isArray(v) ? v.slice(0, max).map(f).filter((x): x is T => x !== null) : []);

function stencil(v: unknown): Stencil {
  const s = obj(v);
  return tidy({ name: str(s?.name, LABEL), strokes: list(s?.strokes, MAX_STROKES, stroke), parts: list(s?.parts, MAX_PARTS, part), mirror: bool(s?.mirror),
    cut: bool(s?.cut), carve: Array.isArray(s?.carve) ? list(s.carve, MAX_CARVES, carve) : undefined });
}

function light(v: unknown): PumpkinLight | undefined {
  const l = obj(v);
  if (!l) return undefined;
  return tidy({ kind: oneOf(l.kind, ['candle', 'candles', 'led', 'blink'] as const), color: rgb(l.color), brightness: num(l.brightness, 0, 3),
    flicker: num(l.flicker, 0, 1), count: num(l.count, 1, 5), rate: num(l.rate, 0, 10) }) as PumpkinLight;
}

function pumpkin(v: unknown): PlacedPumpkin | null {
  const p = obj(v), sh = shape(p?.shape), id = str(p?.id, ID);
  if (!p || !sh || !id || !obj(p.stencil)) return null;
  return tidy({ id, shape: sh, stencil: stencil(p.stencil), light: light(p.light) as PumpkinLight, at: pt(p.at) ?? [0, -0.5],
    turn: req(p.turn, -100, 100, 0), wall: req(p.wall, 0, 1, 0.022), scale: num(p.scale, 0, 10) });
}

function porch(v: unknown): Porch | null {
  const p = obj(v), lamp = obj(p?.lamp);
  if (!p) return null;
  return tidy({ seed: req(p.seed, -2147483648, 4294967296, 1), planks: rgb(p.planks), siding: rgb(p.siding), door: rgb(p.door), trim: rgb(p.trim),
    lamp: lamp ? tidy({ color: rgb(lamp.color), brightness: num(lamp.brightness, 0, 10), on: bool(lamp.on) }) : undefined,
    candy: bool(p.candy), grass: bool(p.grass) }) as Porch;
}

/** the scene with only what the app knows in it, or null if it isn't a scene at all; normaliseScene still runs after */
export function cleanScene(raw: unknown): Scene | null {
  const s = obj(raw), p = porch(s?.porch);
  if (!s || !p || !Array.isArray(s.pumpkins)) return null;
  const c = obj(s.camera), look = obj(s.look);
  const camera = c && Array.isArray(c.target) && c.target.length === 3 && c.target.every(x => typeof x === 'number' && Number.isFinite(x))
    ? tidy({ yaw: num(c.yaw, -100, 100), pitch: num(c.pitch, -100, 100), dist: num(c.dist, 0, 100), target: (c.target as number[]).map(x => Math.min(100, Math.max(-100, x))) as [number, number, number],
      roll: num(c.roll, -4, 4), fov: num(c.fov, 0.1, 3) })
    : undefined;
  return tidy({ version: SCENE_VERSION, title: str(s.title, LABEL), porch: p, pumpkins: list(s.pumpkins, MAX_PUMPKINS, pumpkin),
    camera: camera as Scene['camera'], look: look ? tidy({ filter: num(look.filter, 0, 4) }) as Scene['look'] : undefined });
}
